import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  CHANGE_ORDER_REQUEST,
  POTENTIAL_CHANGE_ORDER,
  PRIME_CHANGE_ORDER,
  PRIME_CONTRACTS,
  PRIME_INVOICES,
  REST_PROJECTS,
} from '../restApi/RestRoutes';
import { RestRequestService } from '../restApi/rest-request.service';
import {
  CorCreateUpdateReq,
  CORDetail,
  CORLogItem,
  PrimeContractModel,
  IPrimeContractModelResponse,
  PcoCorModel,
  PrimeChangeOrderModel,
  PrimeChangeOrderResponse,
  PrimeContractCreateUpdateReq,
  CORLogMetadata,
  PCOCreateDescription,
  COR_STATUS,
} from '../store/prime-commitments/prime-commitments.types';
import {
  PrimeInvoice,
  PrimeInvoiceSaveRequestData,
} from '@app/framework/constants/prime-invoice.constants';
import { PrimeContractSummaryResponse } from '@app/store/prime-commitments/prime-contract-summary.types';
import {
  PcoDetail,
  PcoDetailUpdateReq,
  PcoLogMetadata,
  PcoLogItem,
  PCO_STATUS,
  PcoCreateUpdateReq,
  PcoItem,
  PcoItemCost,
} from '@app/store/pco/pco.types';

@Injectable({
  providedIn: 'root',
})
export class PrimeCommitmentsApiService {
  private readonly rest = inject(RestRequestService);

  /**
   * Load the next PCO number in the sequence
   */
  getNextPcoNumber(projectId: number): Observable<{ next_id: string }> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/next-id/pco`, {}, {});
  }

  /**
   * Load the next PCCO number in the sequence
   */
  getNextPCCONumber(projectId: number): Observable<{ next_id: string }> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/next-id/pcco`, {}, {});
  }

  /**
   * Load the next COR number in the sequence
   */
  getNextCORNumber(projectId: number): Observable<{ next_id: string }> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/next-id/cor`, {}, {});
  }

  getNextInvoiceNumber(projectId: number): Observable<{ next_id: string }> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/next-id/pi`, {}, {});
  }

  isPCCONumberUnique(
    projectId: number,
    pcoNumber: string,
    resource_id: number,
  ): Observable<{ message: string }> {
    return this.rest.postWithObservable(
      `${REST_PROJECTS}/${projectId}/next-id/pcco/validate/${pcoNumber}`,
      { resource_id: resource_id ?? null },
      {},
    );
  }

  isPCONumberUnique(
    projectId: number,
    pcoNumber: string,
    resource_id: number,
  ): Observable<{ message: string }> {
    return this.rest.postWithObservable(
      `${REST_PROJECTS}/${projectId}/next-id/pco/validate/${pcoNumber}`,
      { resource_id: resource_id ?? null },
      {},
    );
  }

  isCORNumberUnique(
    projectId: number,
    corNumber: string,
    resource_id?: number,
  ): Observable<{ message: string }> {
    return this.rest.postWithObservable(
      `${REST_PROJECTS}/${projectId}/next-id/cor/validate/${corNumber}`,
      { resource_id: resource_id ?? null },
      {},
    );
  }

  isPINumberUnique(
    projectId: number,
    piNumber: string,
    resource_id?: number,
  ): Observable<{ message: string }> {
    return this.rest.postWithObservable(
      `${REST_PROJECTS}/${projectId}/next-id/pi/validate/${piNumber}`,
      { resource_id: resource_id ?? null },
      {},
    );
  }

  /**
   * @param projectId
   * @param primeContract
   * Create a prime contract
   */
  createPrimeContract(
    projectId: number,
    primeContract: PrimeContractModel,
  ): Observable<IPrimeContractModelResponse> {
    const body = this.convertToPrimeContractPayload(projectId, primeContract);
    // const strippedBody = this.stripFrontendIds(body);
    return this.rest.postWithObservable(`${PRIME_CONTRACTS}`, body);
  }

  /**
   * Update a prime contract
   * @param projectId
   * @param primeContract
   */
  updatePrimeContract(
    projectId: number,
    primeContract: PrimeContractModel,
  ): Observable<IPrimeContractModelResponse> {
    const body = this.convertToPrimeContractPayload(projectId, primeContract);
    // const strippedBody = this.stripFrontendIds(body);
    return this.rest.putWithObservable(`${PRIME_CONTRACTS}/${primeContract.id}`, body);
  }

  submitPrimeContract(primeContractId: number) {
    return this.rest.postWithObservable(`${PRIME_CONTRACTS}/${primeContractId}/submit`, {});
  }

  /**
   * Loads the prime contract associated with a project.
   * @param projectId
   */
  loadPrimeContractByProjectId(projectId: number): Observable<IPrimeContractModelResponse> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/prime-contracts`);
  }

  /**
   * Loads the prime change order by id
   * @param pcco_id - pcco id
   */
  loadPrimeChangeOrderById(pcco_id: number): Observable<PrimeChangeOrderResponse> {
    return this.rest.getWithObservable(`${PRIME_CHANGE_ORDER}/${pcco_id}`);
  }

  /**
   * Delete a prime contract
   * @param primeContractId
   */
  deletePrimeContract(primeContractId: number) {
    return this.rest.delWithObservable(`${PRIME_CONTRACTS}/${primeContractId}`);
  }

  getPrimeContractViewSummary(projectId: number): Observable<PrimeContractSummaryResponse> {
    return this.rest
      .getWithObservable(`${REST_PROJECTS}/${projectId}/prime-summary`, {}, {})
      .pipe(delay(4000));
  }

  /**
   * @param projectId
   * @param pco - Potential Change Order
   * Create a potential change order
   */
  createPCO(projectId: number, pco: PcoCorModel) {
    const body = this.convertToPCOPayload(projectId, pco);
    return this.rest.postWithObservable(`${POTENTIAL_CHANGE_ORDER}`, body);
  }

  /**
   * Load a potential change order by id
   * @param id
   */
  loadPCODetails(id: number): Observable<PcoDetail> {
    return this.rest.getWithObservable(`${POTENTIAL_CHANGE_ORDER}/${id}`);
  }

  /**
   * Update a potential change order
   * @param PCO
   */
  updatePCODetails(PCO: PcoDetailUpdateReq) {
    return this.rest.putWithObservable(`${POTENTIAL_CHANGE_ORDER}/${PCO.id}`, PCO);
  }

  /**
   * Delete a potential change order by id
   * @param id
   */
  deletePCO(id: number) {
    return this.rest.delWithObservable(`${POTENTIAL_CHANGE_ORDER}/${id}`);
  }

  requestSCO(contractId: number, costID: number | string) {
    return this.rest.postWithObservable(`${POTENTIAL_CHANGE_ORDER}/request-cco`, {
      pco_item_cost_id: costID,
      contract_id: contractId,
    });
  }

  /**
   * Create a change order request
   * @param projectId
   * @param pco
   */
  createCOR(projectId: number, pco: PcoCorModel) {
    const body = this.convertToCORPayload(projectId, pco);
    return this.rest.postWithObservable(`${CHANGE_ORDER_REQUEST}`, body);
  }

  updateCOR(projectId: number, pco: PcoCorModel) {
    const body = this.convertToCORPayload(projectId, pco);
    return this.rest.putWithObservable(`${CHANGE_ORDER_REQUEST}/${pco.generalData.id}`, body);
  }

  updateCORDetails(corDetail: CORDetail) {
    return this.rest.putWithObservable(`${CHANGE_ORDER_REQUEST}/${corDetail.id}`, corDetail);
  }

  loadCORDetails(id: number): Observable<CORDetail> {
    return this.rest.getWithObservable(`${CHANGE_ORDER_REQUEST}/${id}`);
  }

  deleteCORLogItem(id: number) {
    return this.rest.delWithObservable(`${CHANGE_ORDER_REQUEST}/${id}`);
  }

  /**
   * Load all Potential Change Orders
   */
  getAllPCOs(projectId: number, metadata: Partial<PcoLogMetadata>): Observable<PcoLogItem[]> {
    const params = this.getListingMetadataPCO_COR(metadata);

    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/pco`, {}, params);
  }

  /**
   * Load all Change Order Requests (CORs) for a project.
   */
  getAllCORs(projectId: number, metadata: Partial<CORLogMetadata>): Observable<CORLogItem[]> {
    const params = this.getListingMetadataPCO_COR(metadata);

    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/cor`, {}, params);
  }

  /**
   * @param projectId
   * @param pcco - Prime Change Order
   * Create or Update a Prime Commitment Change Order (PCCO)
   */
  savePCCO(projectId: number, pcco: PrimeChangeOrderModel) {
    const body = {
      project_id: projectId,
      title: pcco.generalData.title,
      number: pcco.generalData.number,
      date: pcco.generalData.date,
      status: pcco.generalData.status as unknown as PCO_STATUS,
      description: pcco.generalData.description,
      cors: pcco.cors.map((cor) => ({ id: cor.id })),
    };

    if (pcco.generalData.id) {
      return this.rest.putWithObservable(`${PRIME_CHANGE_ORDER}/${pcco.generalData.id}`, body);
    }

    return this.rest.postWithObservable(`${PRIME_CHANGE_ORDER}`, body);
  }

  deletePCCO(pccoId: number) {
    return this.rest.delWithObservable(`${PRIME_CHANGE_ORDER}/${pccoId}`);
  }

  getAllInvoicesByProject(projectId: number): Observable<any[]> {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/invoices`);
  }

  getInvoice(invoiceId: number): Observable<PrimeInvoice> {
    return this.rest.getWithObservable(`${PRIME_INVOICES}/${invoiceId}`);
  }

  createInvoice(invoice: PrimeInvoiceSaveRequestData): Observable<PrimeInvoice> {
    return this.rest.postWithObservable(PRIME_INVOICES, invoice);
  }

  updateInvoice(invoice: PrimeInvoiceSaveRequestData) {
    return this.rest.putWithObservable(`${PRIME_INVOICES}/${invoice.id}`, invoice);
  }

  deleteInvoice(invoiceId: number) {
    return this.rest.delWithObservable(`${PRIME_INVOICES}/${invoiceId}`);
  }

  getInvoiceSOVData(projectId: number) {
    return this.rest.getWithObservable(`${REST_PROJECTS}/${projectId}/prime-invoices/sov`, {}, {});
  }

  private getListingMetadataPCO_COR<T extends Partial<PcoLogMetadata> | Partial<CORLogMetadata>>(
    metadata: T,
  ): Partial<T> {
    const params: Partial<T> = {
      page: metadata.page,
      perPage: metadata.perPage,
    } as Partial<T>;

    if (metadata.statuses) {
      params['statuses[]'] = metadata.statuses;
    }
    if (metadata.search) {
      params.search = metadata.search;
    }
    if (metadata.sortBy) {
      params.sortBy = metadata.sortBy;
      params.sortDirection = metadata.sortDirection ?? 'asc';
    }
    if (metadata.isAssigned !== null && metadata.isAssigned !== undefined) {
      params.isAssigned = metadata.isAssigned;
    }

    return params;
  }

  private convertToPCOPayload(projectId: number, model: PcoCorModel): PcoCreateUpdateReq {
    return {
      project_id: projectId,
      title: model.generalData.title,
      duration_extension: model.generalData.duration_extension,
      number: model.generalData.number,
      date: model.generalData.date,
      status: model.generalData.status as unknown as PCO_STATUS,
      description: model.generalData.description,
      pco_items: model.lineItems.map((item): Partial<PcoItem> => {
        return {
          item_id: item.id,
          pco_item_costs: item.descriptions.map(
            (description: PCOCreateDescription): PcoItemCost => {
              return {
                pc_spend_description_id: null,
                spend_description: description.spend_description,
                budget_dollars: description.budget_dollars,
                commitment_dollars: 0,
                cco_id: null,
              };
            },
          ),
        };
      }),
    };
  }

  /**
   * Convert the prime contract model to a payload, which can be sent to the server
   * @param projectId
   * @param primeContract
   */
  private convertToPrimeContractPayload(
    projectId: number,
    primeContract: PrimeContractModel,
  ): PrimeContractCreateUpdateReq {
    return {
      project_id: projectId,
      title: primeContract.title,
      owner_name: primeContract.owner_name,
      date: primeContract.date,
      status: primeContract.status,
      description: primeContract.description,
      purchase_order_number: primeContract.purchase_order_number,
      sovs: primeContract.sovs.map((item) => {
        return {
          id: item.id,
          index: item.index,
          name: item.name,
          scheduled_items: item.scheduled_items.map((scheduledItem) => {
            return {
              item_id: scheduledItem.item_id,
              index: scheduledItem.index,
              value: Number(scheduledItem.value),
            };
          }),
        };
      }),
      invoice_retainage: {
        default_rate: Number(primeContract.defaultInvoiceRetainageRate),
        exceptions: primeContract.exceptions,
      },
      markup_groups: primeContract.markupGroups,
    };
  }

  private convertToCORPayload(projectId: number, model: PcoCorModel): CorCreateUpdateReq {
    return {
      project_id: projectId,
      title: model.generalData.title,
      duration_extension: model.generalData.duration_extension,
      number: model.generalData.number,
      date: model.generalData.date,
      status: model.generalData.status as unknown as COR_STATUS,
      description: model.generalData.description,
      pcos: model.selectedPCOIds.map((id): { id: number } => {
        return {
          id,
        };
      }),
    };
  }
}
